import React, { useState, useEffect, useMemo, useRef } from "react";
import { Formik } from "formik";
import { Loadable } from '../../../common';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { Button, Grid, TextField } from "@mui/material";

import makeStyles from '@mui/styles/makeStyles';
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import {
    Portlet,
    PortletBody,
    PortletHeader,
} from "../../../partials/content/Portlet";
import { DocumentView } from '../ContractViewPage/DocumentView';
import { MODULE_TYPE_VENDOR } from "../../../store/constants/contract";
import { Card } from "react-bootstrap";
import { useData } from "@pure-ptr/react";
import { ContractInfo } from "../../../store/api/ai-api";

const useStyles = makeStyles(theme => ({
    button: {
        marginTop: theme.spacing(3),
    },
    leftIcon: {
        marginRight: theme.spacing(1),
    },
    rightIcon: {
        marginLeft: theme.spacing(1),
    },
    iconSmall: {
        fontSize: 20,
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: '85%',
    },
    textArea: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    margin: {
        margin: theme.spacing(1),
    },
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paper,
    },
    formControl: {
        margin: theme.spacing(1),
    },
    title: {
        marginLeft: theme.spacing(1),
        marginTop: theme.spacing(1),
    }
}));

export default function DocumentUpload({
    intl, contract, contractsUploadPending, contractId, handleUploadCallback, module, vendorUploadPending,shouldDeleteDocument=false,
    actions: { contractUpload, vendorUpload } }) {
    const [botUploadPayload, setBotUploadPayload] = useState<any>({});
    const ContractsApi = new ContractInfo()
    


    if( contract ) {
        contractId = contract.id;
    }

    // Compute the actual contract ID to use
    const actualContractId = useMemo(() => {
        return contract?.id || contractId;
    }, [contract?.id, contractId]);

    // const { isPending, data: aiDocsInfo } = useData( async () => {
    //     if(!contract) return
    //     return await contract.info?.fetchDocuments();
    // }, [contract]);


        const {
            isPending: isUplooadingToBot,
            error:botUploadError,
            data: docUpload
        } = useData(async () => {
            if (Object.keys(botUploadPayload)?.length <= 0) return;
    
            let restructuredPayload = botUploadPayload?.docData?.find(
                item => item?.id === botUploadPayload?.id
            );
            restructuredPayload = {
                s3_key: restructuredPayload?.s3Key,
                filename: restructuredPayload?.documentName,
                contract_id: restructuredPayload?.contractId,
                document_id: botUploadPayload?.id,
                metadata: JSON.parse(restructuredPayload?.metadata),
                description: restructuredPayload?.description
            };
    
            setBotUploadPayload({});
    
            return await ContractsApi?.uploadDocumenToBot(
                restructuredPayload?.contract_id,
                restructuredPayload
            );

            
        }, [botUploadPayload]);
    




    const [pollTrigger, setPollTrigger] = useState(0);

    // Phase 1: Initial status check on component mount (handles page refresh)
    const { data: initialDocStatus, isPending: initialStatusPending } = useData(async () => {
        if (!actualContractId) {
            return;
        }
        const statusData = await ContractsApi?.uploadDocumentStatus(actualContractId);
        return statusData;
    }, [actualContractId]);

    // Store the latest document status in a ref to avoid stale closure issues
    const latestDocStatusRef = useRef<any>(null);

    // Helper: returns true if a doc status is still in-progress (not terminal)
    const isInProgress = (status: string) => status !== 'failed' && status !== 'ready' && status !== 'Unknown';

    // Phase 2: Polling - always runs when pollTrigger increments (no stale conditional check)
    const { data: docStatus, isPending: docStatusApi } = useData(async () => {
        if (!actualContractId || pollTrigger === 0) {
            return;
        }
        const statusData = await ContractsApi?.uploadDocumentStatus(actualContractId);
        return statusData;
    }, [pollTrigger, actualContractId]);

    // Trigger an immediate poll right after a new upload completes
    useEffect(() => {
        if (docUpload) {
            setPollTrigger(prev => prev + 1);
        }
    }, [docUpload]);

    // Merge both status sources (initial + post-upload)
    // Always prefer the most recent poll result (docStatus) over the initial check
    const mergedDocStatus = useMemo(() => {
        const current = docStatus || initialDocStatus;
        if (!current) return null;

        if (Array.isArray(current)) return current;
        if (Array.isArray(current.documents)) return current.documents;
        if (current.status) return [{ status: current.status, id: actualContractId }];

        return null;
    }, [docStatus, initialDocStatus, actualContractId]);

    // Update the ref whenever mergedDocStatus changes
    useEffect(() => {
        if (mergedDocStatus) {
            latestDocStatusRef.current = mergedDocStatus;
        }
    }, [mergedDocStatus]);

    // Extract docs that are still in-progress (not 'failed' or 'ready') for polling
    const docStatusesMap = useMemo(() => {
        if (!Array.isArray(mergedDocStatus) || mergedDocStatus.length === 0) {
            return null;
        }
        // Include all docs that are NOT in a terminal state
        const inProgressDocs = mergedDocStatus.filter((doc) => isInProgress(doc.status));
        return inProgressDocs.length > 0 ? inProgressDocs : null;
    }, [mergedDocStatus]);

    // Store interval ID in a ref so we can clear it from anywhere
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Polling mechanism: runs when there are in-progress documents
    useEffect(() => {
        // Clear any existing interval first
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        
        if (!docStatusesMap || docStatusesMap.length === 0) {
            return;
        }

        intervalRef.current = setInterval(() => {
            // Double-check before triggering poll — stop if all docs are now terminal
            if (latestDocStatusRef.current) {
                const stillHasInProgress = latestDocStatusRef.current.some(doc => isInProgress(doc.status));
                if (!stillHasInProgress) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    return;
                }
            }
            setPollTrigger((prev) => prev + 1); // causes useData to re-run
        }, 30000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [docStatusesMap]);

    // Disable fields/buttons ONLY when mergedDocStatus has any doc that is NOT 'failed' or 'ready'
    // Do NOT use docUpload.status here - it's stale after polling updates the real status
    const hasPendingDocs = useMemo(() => {
        if (!mergedDocStatus || mergedDocStatus.length === 0) return false;
        return mergedDocStatus.some(doc => isInProgress(doc.status));
    }, [mergedDocStatus]);


    // TODO: join documents with aiDocsInfo by id

    const classes = useStyles();
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        setFile(event.target.files[0]);
    }

    const handleBotCallBack = (payload) => {
        setBotUploadPayload({ ...payload });

    };

    

    const uploadCallback = (handleBotCallBack,id=null) => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        handleUploadCallback(handleBotCallBack, id);
    }

        useEffect(() => {
            if (!botUploadError) return;
            (async () => {
                return await ContractsApi?.uploadAllDocumentsToBot(contractId, {
                    contract_id: contractId,
                });
            })();
    
        }, [botUploadError]);
    

    return (
        <Loadable loading={contractsUploadPending || vendorUploadPending || isUplooadingToBot!=null}>
            <Formik
                enableReinitialize={true}
                initialValues={
                    {
                        documentName: '',
                        description: '',
                        file: {},
                         documentType: 1
                    }
                }
                validate={values => {
                    const errors: { documentName?: string; file?: string;description?:string } = {};

                    if (!values.documentName) {
                        errors.documentName = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (values.documentName.length > 100) {
                        errors.documentName =
                            "Document name cannot exceed 100 characters";
                    }

                    if (values.description && values.description.length > 500) {
                        errors.description =
                            "Description cannot exceed 500 characters";
                    }

                    if (!file) {
                        errors.file = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }
                    else {
                    const fileName = file.name?.toLowerCase() || "";
                    const fileType = file.type;

                    const isPDF = fileName.endsWith(".pdf") || fileType === "application/pdf";

                    if (values.documentType === 2) {
                        if (!isPDF) {
                            errors.file = "Only PDF files are allowed for Appeal Letter";
                        }
                    } else {
                        const allowedTypes = [
                            "application/pdf",
                            "application/msword",
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        ];
                        const allowedExtensions = [".pdf", ".doc", ".docx"];

                        const hasValidExtension = allowedExtensions.some(ext =>
                            fileName.endsWith(ext)
                        );
                        const hasValidType = allowedTypes.includes(fileType);

                        if (!hasValidExtension || !hasValidType) {
                            errors.file =
                                "Only PDF and Word (.doc, .docx) files are allowed";
                        }
                    }
                }


                    return errors;
                }}
                onSubmit={(values, { resetForm }) => {
                    const fileName = file.name?.toLowerCase() || '';
                    const extension = fileName.substring(fileName.lastIndexOf('.'));

                    const metadata = {
                        type: file.type,
                        file_extension: extension
                    };
                    const documentObj = {
                        contractId,
                        documentName: values.documentName,
                        metadata: JSON.stringify(metadata),
                        description: values.description,
                         documentType: values.documentType,

                    }

                    if (module === MODULE_TYPE_VENDOR) {
                        vendorUpload(file, documentObj, uploadCallback)
                        resetForm();
                    } else {
                        contractUpload(file, documentObj, (id)=> uploadCallback(handleBotCallBack,id));
                        resetForm();
                    }
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting
                }) => (
                    <form onSubmit={handleSubmit} noValidate autoComplete="off">
                        <Card>
                            <Card.Header as="h5">Upload Document</Card.Header>
                            <Card.Body>
                                <Grid container>
                                    <Grid item xs={6} md={3}>
                                        <TextField
                                            required
                                            label="Document Name"
                                            name="documentName"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.documentName}
                                            helperText={touched.documentName && errors.documentName}
                                            error={Boolean(touched.documentName && errors.documentName)}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField
                                            type="text"
                                            multiline
                                            label="Description"
                                            name="description"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            helperText={touched.description && errors.description}
                                            error={Boolean(touched.description && errors.description)}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                    <FormControl className={classes.textField} margin="normal" fullWidth>
                                        <InputLabel>Document Type</InputLabel>
                                        <Select
                                            name="documentType"
                                            value={values.documentType}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        >
                                            <MenuItem value={1}>Contract</MenuItem>
                                            <MenuItem value={2}>Appeal Letter</MenuItem>
                                        </Select>
                                    </FormControl>
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                        <TextField
                                            type="file"
                                            label="Upload Contract"
                                            name="file"
                                            inputRef={fileInputRef}
                                            className={classes.textField}
                                            margin="normal"
                                            onChange={handleFileChange}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            required
                                            helperText={touched.file && errors.file}
                                            error={Boolean(touched.file && errors.file)}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            // type="submit"
                                            onClick={ () => handleSubmit() }
                                            className={classes.button}
                                            startIcon={<CloudUploadIcon />}
                                            disabled={hasPendingDocs || !!docStatusApi}
                                        >
                                            Upload
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DocumentView isEditView={true} contractId={contractId} module={module} shouldDeleteDocument={shouldDeleteDocument}/>
                                    </Grid>
                                </Grid>
                            </Card.Body>
                        </Card>
                    </form>
                )}
            </Formik>
        </Loadable>
    );
}
